import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUpIcon, UserPlusIcon, DollarSignIcon, MousePointerClickIcon, AlertTriangleIcon, BoxIcon } from 'lucide-react';
type Signal = {
  id: number;
  icon: BoxIcon;
  source: string;
  text: string;
  tone: 'up' | 'alert' | 'neutral';
};
const pool: Omit<Signal, 'id'>[] = [{
  icon: DollarSignIcon,
  source: 'Stripe',
  text: 'New $24k ARR deal closed — Acme Corp',
  tone: 'up'
}, {
  icon: MousePointerClickIcon,
  source: 'Google Ads',
  text: 'CPA down 18% on Brand campaign',
  tone: 'up'
}, {
  icon: UserPlusIcon,
  source: 'HubSpot',
  text: 'Enterprise lead hit intent score 92',
  tone: 'neutral'
}, {
  icon: AlertTriangleIcon,
  source: 'Meta Ads',
  text: 'Spend anomaly: +240% on Retargeting',
  tone: 'alert'
}, {
  icon: TrendingUpIcon,
  source: 'GA4',
  text: 'Signup conversion up 6.4% WoW',
  tone: 'up'
}, {
  icon: UserPlusIcon,
  source: 'Salesforce',
  text: 'Opportunity moved to Negotiation',
  tone: 'neutral'
}];
const toneStyles: Record<Signal['tone'], string> = {
  up: 'bg-signal/10 text-signal',
  alert: 'bg-ink text-signal',
  neutral: 'bg-ink/5 text-ink'
};
export function Showcase() {
  const [feed, setFeed] = useState<Signal[]>(pool.slice(0, 4).map((s, i) => ({
    ...s,
    id: i
  })));
  useEffect(() => {
    let counter = feed.length;
    const interval = setInterval(() => {
      const next = pool[Math.floor(Math.random() * pool.length)];
      setFeed(prev => [{
        ...next,
        id: counter++
      }, ...prev].slice(0, 5));
    }, 2600);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <section id="showcase" className="py-20 md:py-28" data-id="element-165">
      <div className="mx-auto max-w-[1200px] px-5 md:px-8" data-id="element-166">
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-20" data-id="element-167">
          <div data-id="element-168">
            <p className="text-xs font-semibold uppercase tracking-widest text-signal" data-id="element-169">
              The signal feed
            </p>
            <h2 className="mt-4 font-display text-4xl font-semibold leading-[1.05] tracking-tight md:text-6xl" data-id="element-170">
              Watch your entire funnel move, live.
            </h2>
            <p className="mt-5 text-lg text-ink/60" data-id="element-171">
              Every meaningful event from every tool, streamed into one feed and
              enriched with context. Filter by source, score or team — or let
              alerts bring the signal to you.
            </p>
            <ul className="mt-8 space-y-3" data-id="element-172">
              {['Unified across 14+ live sources', 'Enriched with account & attribution context', 'Routable to Slack, email or webhook'].map(item => <li key={item} className="flex items-center gap-3 text-ink/80" data-id="element-173">
                  <span className="h-1.5 w-1.5 rounded-full bg-signal" data-id="element-174" />
                  {item}
                </li>)}
            </ul>
          </div>

          <div className="rounded-3xl border border-ink/15 bg-white p-4 shadow-[0_30px_80px_-40px_rgba(17,17,17,0.4)] md:p-5" data-id="element-175">
            <div className="flex items-center justify-between border-b border-ink/10 px-2 pb-4" data-id="element-176">
              <div className="flex items-center gap-2" data-id="element-177">
                <span className="relative flex h-2.5 w-2.5" data-id="element-178">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-signal opacity-60" data-id="element-179" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-signal" data-id="element-180" />
                </span>
                <span className="text-sm font-semibold" data-id="element-181">Live signal feed</span>
              </div>
              <span className="rounded-full bg-cream px-2.5 py-1 text-xs font-medium text-ink/60" data-id="element-182">
                All sources
              </span>
            </div>

            <div className="mt-2 flex flex-col gap-2" data-id="element-183">
              <AnimatePresence initial={false} data-id="element-184">
                {feed.map(s => <motion.div key={s.id} layout initial={{
                opacity: 0,
                y: -14,
                scale: 0.98
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.4,
                ease: 'easeOut'
              }} className="flex items-center gap-3 rounded-xl border border-ink/10 bg-cream/60 p-3" data-id="element-185">
                    <span className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${toneStyles[s.tone]}`} data-id="element-186">
                      <s.icon className="h-4 w-4" data-id="element-187" />
                    </span>
                    <div className="min-w-0" data-id="element-188">
                      <p className="truncate text-sm font-medium text-ink" data-id="element-189">
                        {s.text}
                      </p>
                      <p className="text-xs text-ink/50" data-id="element-190">{s.source}</p>
                    </div>
                  </motion.div>)}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>;
}